import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { Document3Icon, GridIcon, Info3Icon, RubleIcon, User2Icon } from "@/shared/icons";
import Loader from "@/shared/ui/Loader";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import { factionsList } from "@/domains/faction/helpers/factionsList";
import useGetInfo from "@/domains/faction/hooks/useGetInfo";
import { useLeaveFaction } from "@/domains/faction/hooks/useLeaveFromFaction";
import { useUpgradeRank } from "@/domains/faction/hooks/useUpgradeRank";
import FactionImprovementsContainer from "@/domains/faction/containers/FactionImprovementsContainer";
import FactionConfirmModal from "@/domains/faction/components/FactionConfirmModal";
import FactionExperience from "@/domains/faction/components/FactionExperience";
import FactionInfoAside from "@/domains/faction/components/FactionInfoAside";
import FactionParticipantCard from "@/domains/faction/components/FactionParticipantCard";
import FactionStatusCard from "@/domains/faction/components/FactionStatusCard";
import FactionTextWrapper from "@/domains/faction/components/FactionTextWrapper";

type Props = {
  faction: FactionEnum;
};

export default function FactionInformationContainer({ faction }: Props) {
  const { t } = useTranslation("faction");
  const { mutate: leave, isPending: isLeavePending } = useLeaveFaction();
  const { mutate: update, isPending: isUpdatePending } = useUpgradeRank();

  const [isDismissModal, setDismissModal] = useState(false);
  const [isUpRankModal, setIsUpRankModal] = useState(false);
  const [isImprovementsScreen, setIsImprovementsScreen] = useState(false);
  const [improvementsCount, setImprovementsCount] = useState(24);

  const { data, isLoading } = useGetInfo();

  const onLeave = useCallback(() => {
    leave();
    setDismissModal(false);
  }, [leave]);

  const onUpdate = useCallback(() => {
    update();
    setIsUpRankModal(false);
  }, [update]);

  const highRankMembers = useMemo(() => {
    const members = data?.highRankMembers || [];
    if (!members.length) return [];

    const positions = Object.fromEntries(
      (data?.rankPositions || []).map((item) => [item.id, item.name]),
    );
    const ranks = Object.fromEntries((data?.ranks || []).map((item) => [item.id, item]));

    const enhancedMembers = members
      .map((item) => {
        const rank = ranks[item.rankId];
        const positionName = positions[item.rankPositionId];
        if (!rank || !positionName) return null;

        return {
          ...item,
          rankPositionName: positionName,
          rankName: rank.name,
          rankLevel: rank.level,
        };
      })
      .filter(Boolean);

    enhancedMembers.sort((a, b) => (b.rankLevel || 0) - (a.rankLevel || 0));

    return enhancedMembers.slice(0, 3);
  }, [data]);

  const currentTasks = useMemo(() => {
    if (!data?.rankTasks) return 0;
    return data.rankTasks.filter((item) => item.isCompleted).length;
  }, [data?.rankTasks]);

  return (
    <div className="flex flex-col grow p-6.5">
      {isLoading && <Loader />}
      {data && !isLoading && (
        <div className="flex size-full items-start">
          <FactionInfoAside
            src="/img/faction/army/army.jpg"
            data={data}
            color={factionsList[faction].color}
            onDismiss={() => setDismissModal(true)}
          />
          <div className="relative flex grow flex-col pl-6">
            <div className="flex items-center gap-2 mb-3.5">
              <Info3Icon className="size-5 text-white/30" />
              <div className="text-14 font-bold uppercase">{t("faction.titles.information")}</div>
            </div>

            <div className="grid grid-cols-2 gap-3.5 w-full mb-3.5">
              <FactionTextWrapper color={factionsList[faction].color}>
                <RubleIcon className="size-4.5 text-accent-solid" />
                <div className="text-14 font-bold leading-none">
                  {formatPrice(data?.moneyBalance)} ₽
                </div>
                <div className="ml-auto text-11 font-bold text-white/70 uppercase leading-none">
                  {t("faction.fractionBalance")}
                </div>
              </FactionTextWrapper>
              <FactionTextWrapper type="right" color={factionsList[faction].color}>
                <div className="text-11 font-bold text-white/70 uppercase leading-none">
                  {t("faction.fractionParticipants")}
                </div>
                <div className="ml-auto text-14 font-bold leading-none">
                  {formatPrice(data?.membersCount)}
                </div>
                <User2Icon className="size-4.5 text-accent-solid" />
              </FactionTextWrapper>
            </div>

            <div className="grid grid-cols-3 gap-3.5 mb-3.5">
              {highRankMembers.length > 0 &&
                highRankMembers.map((item) => (
                  <FactionParticipantCard
                    key={item.id}
                    src=""
                    id={item.id}
                    name={`${item.firstName} ${item.lastName}`}
                    rank={item.rankName}
                    position={item.rankPositionName}
                  />
                ))}
            </div>

            {data?.rankTasks?.length > 0 && (
              <FactionExperience
                tasksList={data?.rankTasks}
                src={factionsList[faction].experienceSrc}
                currentCount={currentTasks}
                nextLevelCount={data?.rankTasks?.length}
                onClick={() => setIsUpRankModal(true)}
                factionName={faction}
                className="mb-3.5"
              />
            )}

            <div className="grid grid-cols-2 gap-3.5">
              <FactionStatusCard
                i18key="improvements"
                count={0}
                Icon={GridIcon}
                faction={faction}
                onClick={() => setIsImprovementsScreen(true)}
                isSoon
              />
              <FactionStatusCard
                i18key="orders"
                count={0}
                Icon={Document3Icon}
                faction={faction}
                onClick={() => {}}
                isSoon
              />
            </div>
          </div>
        </div>
      )}
      {isDismissModal && (
        <FactionConfirmModal
          title={t("faction.modals.dismissSelf.title")}
          description={t("faction.modals.dismissSelf.description")}
          onClose={() => setDismissModal(false)}
          onConfirm={onLeave}
          isConfirming={isLeavePending}
          faction={faction}
        />
      )}
      {isUpRankModal && (
        <FactionConfirmModal
          title={t("faction.modals.upRank.title")}
          description={t("faction.modals.upRank.description")}
          onClose={() => setIsUpRankModal(false)}
          onConfirm={onUpdate}
          isConfirming={isUpdatePending}
          faction={faction}
        />
      )}
      {isImprovementsScreen && (
        <FactionImprovementsContainer
          currentCount={improvementsCount}
          setCount={setImprovementsCount}
          maxCount={24}
          faction={faction}
          onBack={() => setIsImprovementsScreen(false)}
        />
      )}
    </div>
  );
}
